import React, { useState, useEffect, useRef, useCallback } from 'react';
import { HashRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';

/* ═══════════════════════════════════════════════════
   STYLES & THEME
   ═══════════════════════════════════════════════════ */
const Styles = () => (
  <style>{`
    *,*::before,*::after{margin:0;padding:0;box-sizing:border-box}
    :root{
      --gold:#c9a84c;--gold-light:#e8d48b;--gold-dark:#9a7a2e;
      --emerald:#10b981;--emerald-dark:#047857;
      --bg:#08090d;--bg2:#0e1019;--bg3:#141620;
      --card:rgba(14,16,25,0.85);--card-hover:rgba(20,22,32,0.95);
      --text:#e8e6e0;--text-dim:#8a8b94;--text-muted:#5a5b64;
      --border:rgba(201,168,76,0.12);--border-hover:rgba(201,168,76,0.35);
      --glow-gold:0 0 40px rgba(201,168,76,0.15);
      --glow-emerald:0 0 40px rgba(16,185,129,0.15);
    }
    html{scroll-behavior:smooth}
    body{font-family:'DM Sans',sans-serif;background:var(--bg);color:var(--text);overflow-x:hidden;line-height:1.65}
    a{text-decoration:none;color:inherit}
    ::-webkit-scrollbar{width:5px}
    ::-webkit-scrollbar-track{background:var(--bg)}
    ::-webkit-scrollbar-thumb{background:var(--gold-dark);border-radius:3px}

    @keyframes fadeUp{from{opacity:0;transform:translateY(50px)}to{opacity:1;transform:translateY(0)}}
    @keyframes fadeIn{from{opacity:0}to{opacity:1}}
    @keyframes slideRight{from{opacity:0;transform:translateX(-40px)}to{opacity:1;transform:translateX(0)}}
    @keyframes shimmer{0%{background-position:-200% 0}100%{background-position:200% 0}}
    @keyframes float{0%,100%{transform:translateY(0)}50%{transform:translateY(-15px)}}
    @keyframes pulse{0%,100%{opacity:1}50%{opacity:.5}}
    @keyframes rotate{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
    @keyframes goldGlow{0%,100%{box-shadow:0 0 20px rgba(201,168,76,0.1)}50%{box-shadow:0 0 50px rgba(201,168,76,0.3)}}
    @keyframes scaleIn{from{opacity:0;transform:scale(.9)}to{opacity:1;transform:scale(1)}}
    @keyframes grain{0%,100%{transform:translate(0,0)}10%{transform:translate(-5%,-10%)}20%{transform:translate(-15%,5%)}30%{transform:translate(7%,-25%)}40%{transform:translate(-5%,25%)}50%{transform:translate(-15%,10%)}60%{transform:translate(15%,0%)}70%{transform:translate(0%,15%)}80%{transform:translate(3%,35%)}90%{transform:translate(-10%,10%)}}
    @keyframes marquee{from{transform:translateX(0)}to{transform:translateX(-50%)}}
    @keyframes countPulse{0%{transform:scale(1)}50%{transform:scale(1.05)}100%{transform:scale(1)}}

    .anim{opacity:0;transform:translateY(40px);transition:all .9s cubic-bezier(.16,1,.3,1)}
    .anim.vis{opacity:1;transform:translateY(0)}
    .anim-d1{transition-delay:.1s}.anim-d2{transition-delay:.2s}.anim-d3{transition-delay:.3s}
    .anim-d4{transition-delay:.4s}.anim-d5{transition-delay:.5s}.anim-d6{transition-delay:.6s}

    .gold-text{background:linear-gradient(135deg,var(--gold-light),var(--gold),var(--gold-dark));-webkit-background-clip:text;-webkit-text-fill-color:transparent}
    .gold-border{border:1px solid var(--border);transition:border-color .4s}
    .gold-border:hover{border-color:var(--border-hover)}

    .grain-overlay{position:fixed;top:-50%;left:-50%;right:-50%;bottom:-50%;width:200%;height:200%;
      background:url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.03'/%3E%3C/svg%3E");
      pointer-events:none;z-index:9999;animation:grain 8s steps(10) infinite;opacity:.4}

    @media(max-width:768px){
      .hide-mobile{display:none!important}
      .show-mobile{display:flex!important}
      .grid-responsive{grid-template-columns:1fr!important}
      .hero-title{font-size:36px!important}
      .section-pad{padding:60px 5%!important}
    }
  `}</style>
);

/* ═══════════════════════════════════════════════════
   HOOKS
   ═══════════════════════════════════════════════════ */
function useScrollAnim() {
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('vis'); }),
      { threshold: 0.08 }
    );
    document.querySelectorAll('.anim').forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);
}

function useCounter(end, duration = 2000) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !started.current) {
        started.current = true;
        let s = 0; const step = end / (duration / 16);
        const t = setInterval(() => { s += step; if (s >= end) { setVal(end); clearInterval(t); } else setVal(Math.floor(s)); }, 16);
      }
    }, { threshold: 0.5 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [end, duration]);
  return [val, ref];
}

/* ═══════════════════════════════════════════════════
   SHARED COMPONENTS
   ═══════════════════════════════════════════════════ */
const DiamondIcon = ({ size = 32 }) => (
  <svg width={size} height={size} viewBox="0 0 100 100">
    <defs>
      <linearGradient id="dg" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#e8d48b" /><stop offset="50%" stopColor="#c9a84c" /><stop offset="100%" stopColor="#9a7a2e" />
      </linearGradient>
    </defs>
    <polygon points="50,8 85,40 50,92 15,40" fill="none" stroke="url(#dg)" strokeWidth="3.5" />
    <polygon points="50,8 85,40 50,42 15,40" fill="none" stroke="url(#dg)" strokeWidth="2" opacity=".5" />
    <line x1="50" y1="8" x2="50" y2="92" stroke="url(#dg)" strokeWidth="1.5" opacity=".3" />
  </svg>
);

const Logo = () => (
  <Link to="/" style={{ display:'flex', alignItems:'center', gap:10 }}>
    <DiamondIcon size={28} />
    <span style={{ fontFamily:'Playfair Display', fontWeight:700, fontSize:20, letterSpacing:1 }}>
      <span className="gold-text">Hidden</span><span style={{color:'#fff'}}>Rock</span>
    </span>
  </Link>
);

const Badge = ({ children }) => (
  <span style={{
    fontFamily:'JetBrains Mono', fontSize:11, letterSpacing:3, textTransform:'uppercase',
    color:'var(--gold)', padding:'8px 18px', border:'1px solid var(--border)', borderRadius:24,
    display:'inline-flex', alignItems:'center', gap:8,
  }}>
    <span style={{ width:6, height:6, borderRadius:'50%', background:'var(--emerald)', animation:'pulse 2s infinite' }} />
    {children}
  </span>
);

const SectionHead = ({ badge, title, sub }) => (
  <div className="anim" style={{ textAlign:'center', marginBottom:56, maxWidth:700, marginLeft:'auto', marginRight:'auto' }}>
    {badge && <div style={{ marginBottom:16 }}><Badge>{badge}</Badge></div>}
    <h2 style={{ fontFamily:'Playfair Display', fontSize:'clamp(28px,4.5vw,52px)', fontWeight:800, lineHeight:1.15, marginBottom:16 }}>
      <span className="gold-text">{title}</span>
    </h2>
    {sub && <p style={{ color:'var(--text-dim)', fontSize:17, lineHeight:1.7 }}>{sub}</p>}
  </div>
);

const Card = ({ children, style = {}, delay = '' }) => (
  <div className={`anim ${delay} gold-border`} style={{
    background:'var(--card)', borderRadius:16, padding:32, position:'relative', overflow:'hidden',
    transition:'all .4s ease, border-color .4s', cursor:'default', ...style,
  }}
  onMouseOver={e=>{e.currentTarget.style.background='var(--card-hover)';e.currentTarget.style.transform='translateY(-6px)';e.currentTarget.style.boxShadow='var(--glow-gold)';}}
  onMouseOut={e=>{e.currentTarget.style.background='var(--card)';e.currentTarget.style.transform='translateY(0)';e.currentTarget.style.boxShadow='none';}}>
    {children}
  </div>
);

const Btn = ({ children, primary, href, to, style: s = {}, ...props }) => {
  const base = {
    display:'inline-flex', alignItems:'center', gap:8, padding:'14px 32px', borderRadius:10,
    fontFamily:'DM Sans', fontWeight:600, fontSize:14, letterSpacing:.5, cursor:'pointer',
    transition:'all .35s ease', border:'none', ...s,
    ...(primary ? {
      background:'linear-gradient(135deg, var(--gold-light), var(--gold), var(--gold-dark))',
      color:'#08090d',
    } : {
      background:'transparent', border:'1px solid var(--gold)', color:'var(--gold)',
    })
  };
  if (to) return <Link to={to} style={base} {...props}>{children}</Link>;
  if (href) return <a href={href} target="_blank" rel="noopener noreferrer" style={base} {...props}>{children}</a>;
  return <button style={base} {...props}>{children}</button>;
};

const Stat = ({ value, label, suffix = '' }) => {
  const [v, ref] = useCounter(parseInt(value));
  return (
    <div ref={ref} style={{ textAlign:'center' }}>
      <div style={{ fontFamily:'Playfair Display', fontSize:'clamp(32px,5vw,54px)', fontWeight:800 }} className="gold-text">
        {v.toLocaleString()}{suffix}
      </div>
      <div style={{ fontFamily:'DM Sans', fontSize:14, color:'var(--text-dim)', marginTop:8, letterSpacing:1 }}>{label}</div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   NAVBAR
   ═══════════════════════════════════════════════════ */
const navLinks = [
  { path:'/', label:'Home' },
  { path:'/about', label:'About' },
  { path:'/assets', label:'Assets' },
  { path:'/how-it-works', label:'How It Works' },
  { path:'/tokenization', label:'Tokenization' },
  { path:'/investors', label:'Investors' },
  { path:'/marketplace', label:'Marketplace' },
  { path:'/contact', label:'Contact' },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', h); return () => window.removeEventListener('scroll', h);
  }, []);
  useEffect(() => setOpen(false), [loc]);

  return (
    <nav style={{
      position:'fixed', top:0, left:0, right:0, zIndex:1000,
      background: scrolled ? 'rgba(8,9,13,0.95)' : 'transparent',
      backdropFilter: scrolled ? 'blur(20px)' : 'none',
      borderBottom: scrolled ? '1px solid var(--border)' : 'none',
      transition:'all .4s', padding:'0 5%',
    }}>
      <div style={{ maxWidth:1320, margin:'0 auto', display:'flex', alignItems:'center', justifyContent:'space-between', height: scrolled ? 64 : 80, transition:'height .4s' }}>
        <Logo />
        <div className="hide-mobile" style={{ display:'flex', gap:28, alignItems:'center' }}>
          {navLinks.map(l => (
            <Link key={l.path} to={l.path} style={{
              fontFamily:'DM Sans', fontWeight:500, fontSize:13.5, letterSpacing:.3,
              color: loc.pathname === l.path ? 'var(--gold)' : 'var(--text-dim)',
              transition:'color .3s', position:'relative',
            }}
            onMouseOver={e=>e.target.style.color='var(--gold)'}
            onMouseOut={e=>{if(loc.pathname!==l.path)e.target.style.color='var(--text-dim)'}}>
              {l.label}
            </Link>
          ))}
          <Btn primary href="https://bricsx.io" style={{ padding:'10px 22px', fontSize:12, borderRadius:8 }}>Launch App</Btn>
        </div>
        <button className="show-mobile" onClick={()=>setOpen(!open)} style={{
          display:'none', background:'none', border:'none', cursor:'pointer', flexDirection:'column', gap:5, padding:8,
        }}>
          {[0,1,2].map(i=><span key={i} style={{
            width:22, height:2, background:'var(--gold)', borderRadius:2, transition:'all .3s',
            transform: open ? (i===0?'rotate(45deg) translateY(7px)':i===1?'scaleX(0)':'rotate(-45deg) translateY(-7px)') : 'none',
          }} />)}
        </button>
      </div>
      {open && (
        <div style={{
          position:'absolute', top:'100%', left:0, right:0, background:'rgba(8,9,13,.98)',
          backdropFilter:'blur(20px)', borderBottom:'1px solid var(--border)', padding:'20px 5%',
          animation:'fadeUp .3s ease',
        }}>
          {navLinks.map(l=>(
            <Link key={l.path} to={l.path} style={{
              display:'block', padding:'12px 0', fontFamily:'DM Sans', fontWeight:500, fontSize:16,
              color: loc.pathname===l.path ? 'var(--gold)' : 'var(--text-dim)',
              borderBottom:'1px solid var(--border)',
            }}>{l.label}</Link>
          ))}
        </div>
      )}
    </nav>
  );
};

/* ═══════════════════════════════════════════════════
   FOOTER
   ═══════════════════════════════════════════════════ */
const Footer = () => (
  <footer style={{ background:'var(--bg2)', borderTop:'1px solid var(--border)', padding:'64px 5% 32px' }}>
    <div style={{ maxWidth:1320, margin:'0 auto' }}>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(200px, 1fr))', gap:40, marginBottom:48 }}>
        <div>
          <Logo />
          <p style={{ color:'var(--text-dim)', fontSize:14, lineHeight:1.8, marginTop:16, maxWidth:280 }}>
            Tokenizing real-world assets on the COREX blockchain. Property, Gold, Bonds & more — powered by CRX-20 & BRX.
          </p>
        </div>
        {[
          { title:'Platform', items:['Marketplace','Property','Gold & Metals','Bonds','Loans'] },
          { title:'Company', items:['About Us','How It Works','Investors','Whitepaper','Careers'] },
          { title:'Resources', items:['Documentation','CRX Blockchain','BRX Token','BRICSX.io','Blog'] },
          { title:'Legal', items:['Privacy Policy','Terms of Service','Risk Disclosure','Compliance','KYC/AML'] },
        ].map(col=>(
          <div key={col.title}>
            <h4 style={{ fontFamily:'Playfair Display', fontSize:15, fontWeight:700, color:'var(--gold)', marginBottom:16 }}>{col.title}</h4>
            {col.items.map(i=>(
              <div key={i} style={{ color:'var(--text-dim)', fontSize:13, padding:'5px 0', cursor:'pointer', transition:'color .3s' }}
                onMouseOver={e=>e.target.style.color='var(--gold)'} onMouseOut={e=>e.target.style.color='var(--text-dim)'}>{i}</div>
            ))}
          </div>
        ))}
      </div>
      <div style={{ borderTop:'1px solid var(--border)', paddingTop:24, display:'flex', flexWrap:'wrap', justifyContent:'space-between', gap:16, alignItems:'center' }}>
        <p style={{ color:'var(--text-muted)', fontSize:12 }}>© 2024 HiddenRock.io — Powered by <a href="https://corexnetwork.org" style={{ color:'var(--gold)' }}>COREX Network</a> & <a href="https://bricsx.io" style={{ color:'var(--gold)' }}>BRICSX.io</a></p>
        <div style={{ display:'flex', gap:16 }}>
          {['Twitter','Telegram','Discord','LinkedIn'].map(s=>(
            <span key={s} style={{ color:'var(--text-muted)', fontSize:12, cursor:'pointer', transition:'color .3s' }}
              onMouseOver={e=>e.target.style.color='var(--gold)'} onMouseOut={e=>e.target.style.color='var(--text-muted)'}>{s}</span>
          ))}
        </div>
      </div>
    </div>
  </footer>
);

/* ═══════════════════════════════════════════════════
   MARQUEE BANNER
   ═══════════════════════════════════════════════════ */
const Marquee = () => (
  <div style={{ overflow:'hidden', borderTop:'1px solid var(--border)', borderBottom:'1px solid var(--border)', padding:'14px 0', background:'rgba(201,168,76,0.03)' }}>
    <div style={{ display:'flex', animation:'marquee 30s linear infinite', whiteSpace:'nowrap' }}>
      {[...Array(2)].map((_,k)=>(
        <div key={k} style={{ display:'flex', gap:48, marginRight:48 }}>
          {['PROPERTY TOKENIZATION','GOLD BACKED TOKENS','BOND TOKENIZATION','LOAN TOKENIZATION','CRX-20 STANDARD','BRX PAYMENTS','FRACTIONAL OWNERSHIP','RENTAL INCOME SHARING','DEFI YIELD','SMART CONTRACTS'].map(t=>(
            <span key={t+k} style={{ fontFamily:'JetBrains Mono', fontSize:11, letterSpacing:3, color:'var(--gold-dark)' }}>◆ {t}</span>
          ))}
        </div>
      ))}
    </div>
  </div>
);

/* ═══════════════════════════════════════════════════
   PAGE 1: HOME
   ═══════════════════════════════════════════════════ */
const HomePage = () => {
  useScrollAnim();
  return (
    <div>
      {/* HERO */}
      <section style={{ minHeight:'100vh', display:'flex', alignItems:'center', position:'relative', padding:'120px 5% 80px', overflow:'hidden' }}>
        {/* Background Effects */}
        <div style={{ position:'absolute', top:'-20%', right:'-10%', width:700, height:700, borderRadius:'50%', background:'radial-gradient(circle, rgba(201,168,76,0.06) 0%, transparent 70%)', animation:'float 10s ease-in-out infinite' }} />
        <div style={{ position:'absolute', bottom:'0%', left:'-10%', width:500, height:500, borderRadius:'50%', background:'radial-gradient(circle, rgba(16,185,129,0.04) 0%, transparent 70%)', animation:'float 12s ease-in-out infinite 3s' }} />
        {/* Grid lines */}
        <div style={{ position:'absolute', inset:0, opacity:.03, backgroundImage:'linear-gradient(var(--gold) 1px, transparent 1px), linear-gradient(90deg, var(--gold) 1px, transparent 1px)', backgroundSize:'80px 80px' }} />

        <div style={{ maxWidth:1320, margin:'0 auto', display:'grid', gridTemplateColumns:'1fr 1fr', gap:60, alignItems:'center', width:'100%' }} className="grid-responsive">
          <div style={{ animation:'fadeUp 1s ease' }}>
            <div style={{ marginBottom:24 }}><Badge>CRX BLOCKCHAIN POWERED</Badge></div>
            <h1 className="hero-title" style={{ fontFamily:'Playfair Display', fontSize:'clamp(38px,5.5vw,68px)', fontWeight:900, lineHeight:1.08, marginBottom:24 }}>
              <span style={{ color:'#fff' }}>Tokenize</span><br/>
              <span className="gold-text">Real World Assets</span>
            </h1>
            <p style={{ fontSize:18, color:'var(--text-dim)', lineHeight:1.75, marginBottom:36, maxWidth:480 }}>
              Invest in Property, Gold, Bonds & Loans through blockchain tokenization.
              Earn rental income & profits in <span style={{color:'var(--gold)',fontWeight:600}}>BRX</span> tokens on the <span style={{color:'var(--emerald)',fontWeight:600}}>COREX Network</span>.
            </p>
            <div style={{ display:'flex', gap:14, flexWrap:'wrap' }}>
              <Btn primary to="/marketplace">Explore Assets →</Btn>
              <Btn to="/how-it-works">How It Works</Btn>
            </div>
            <div style={{ display:'flex', gap:32, marginTop:48, flexWrap:'wrap' }}>
              {[
                { v:'$250M+', l:'Assets Tokenized' },
                { v:'12,000+', l:'Investors' },
                { v:'18%', l:'Avg. Annual Return' },
              ].map(s=>(
                <div key={s.l}>
                  <div style={{ fontFamily:'Playfair Display', fontSize:24, fontWeight:800 }} className="gold-text">{s.v}</div>
                  <div style={{ fontSize:13, color:'var(--text-dim)', marginTop:4 }}>{s.l}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="hide-mobile" style={{ position:'relative', animation:'fadeIn 1.2s ease .3s both' }}>
            {/* Hero visual — abstract tokenized property card */}
            <div style={{ background:'var(--card)', border:'1px solid var(--border)', borderRadius:24, padding:36, position:'relative', animation:'goldGlow 4s ease-in-out infinite' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24 }}>
                <Badge>LIVE ASSET</Badge>
                <span style={{ color:'var(--emerald)', fontSize:13, fontFamily:'JetBrains Mono' }}>● Active</span>
              </div>
              <div style={{ fontSize:14, color:'var(--text-dim)', marginBottom:8 }}>Luxury Villa — Dubai Marina</div>
              <div style={{ fontFamily:'Playfair Display', fontSize:36, fontWeight:800, color:'#fff', marginBottom:20 }}>$2,500,000</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:24 }}>
                {[
                  { k:'Token Price', v:'250 BRX' },
                  { k:'Total Tokens', v:'10,000' },
                  { k:'Annual Yield', v:'12.5%' },
                  { k:'Tokens Sold', v:'7,842' },
                ].map(r=>(
                  <div key={r.k} style={{ background:'rgba(201,168,76,0.05)', borderRadius:10, padding:'12px 14px', border:'1px solid var(--border)' }}>
                    <div style={{ fontSize:11, color:'var(--text-muted)', fontFamily:'JetBrains Mono', letterSpacing:1 }}>{r.k}</div>
                    <div style={{ fontSize:16, fontWeight:700, color:'var(--gold)', marginTop:4 }}>{r.v}</div>
                  </div>
                ))}
              </div>
              {/* Progress bar */}
              <div style={{ marginBottom:16 }}>
                <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, color:'var(--text-dim)', marginBottom:6 }}>
                  <span>Funding Progress</span><span style={{color:'var(--gold)'}}>78.4%</span>
                </div>
                <div style={{ height:6, borderRadius:3, background:'rgba(201,168,76,0.1)' }}>
                  <div style={{ width:'78.4%', height:'100%', borderRadius:3, background:'linear-gradient(90deg, var(--gold-dark), var(--gold), var(--gold-light))', transition:'width 2s ease' }} />
                </div>
              </div>
              <div style={{ display:'flex', gap:8 }}>
                {['CRX-20','Property','Dubai','Rental Income'].map(t=>(
                  <span key={t} style={{ padding:'4px 10px', borderRadius:6, fontSize:10, fontFamily:'JetBrains Mono', letterSpacing:1, border:'1px solid var(--border)', color:'var(--text-dim)' }}>{t}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Marquee />

      {/* STATS */}
      <section style={{ padding:'64px 5%' }}>
        <div style={{ maxWidth:1100, margin:'0 auto', display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(180px, 1fr))', gap:32 }}>
          <Stat value="250" label="Assets Tokenized" suffix="+" />
          <Stat value="12000" label="Global Investors" suffix="+" />
          <Stat value="50" label="Countries" suffix="+" />
          <Stat value="18" label="Avg. Return %" suffix="%" />
        </div>
      </section>

      {/* ASSET CATEGORIES */}
      <section className="section-pad" style={{ padding:'80px 5%' }}>
        <div style={{ maxWidth:1320, margin:'0 auto' }}>
          <SectionHead badge="Asset Classes" title="What Can You Tokenize?" sub="From real estate to precious metals — HiddenRock brings traditional assets to the blockchain through CRX-20 tokenization." />
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(280px, 1fr))', gap:20 }}>
            {[
              { icon:'🏢', title:'Real Estate', desc:'Commercial & residential properties tokenized into fractional shares. Earn rental income distributed as BRX tokens.', tag:'High Demand' },
              { icon:'🏆', title:'Gold & Precious Metals', desc:'Physical gold vaults tokenized on CRX-20. Each token backed by real gold. Trade anytime, anywhere.', tag:'Stable' },
              { icon:'📜', title:'Government Bonds', desc:'Tokenized sovereign bonds offering fixed returns. Steady income paid in BRX with full transparency.', tag:'Low Risk' },
              { icon:'💳', title:'Loans & Debt', desc:'Participate in tokenized loan pools. Earn interest from borrowers, distributed automatically via smart contracts.', tag:'Yield' },
              { icon:'🎨', title:'Art & Collectibles', desc:'Fine art and rare collectibles fractionalized. Own a piece of a Picasso or a vintage Ferrari.', tag:'Coming Soon' },
              { icon:'⚡', title:'Infrastructure', desc:'Energy plants, toll roads, and data centers. Earn from real infrastructure revenue streams.', tag:'Coming Soon' },
            ].map((a,i) => (
              <Card key={i} delay={`anim-d${i+1}`}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:16 }}>
                  <span style={{ fontSize:40 }}>{a.icon}</span>
                  <span style={{
                    padding:'4px 10px', borderRadius:6, fontSize:10, fontFamily:'JetBrains Mono', letterSpacing:1,
                    background: a.tag==='Coming Soon' ? 'rgba(138,139,148,0.1)' : 'rgba(201,168,76,0.1)',
                    color: a.tag==='Coming Soon' ? 'var(--text-dim)' : 'var(--gold)',
                    border: `1px solid ${a.tag==='Coming Soon' ? 'rgba(138,139,148,0.2)' : 'var(--border)'}`,
                  }}>{a.tag}</span>
                </div>
                <h3 style={{ fontFamily:'Playfair Display', fontSize:20, fontWeight:700, color:'#fff', marginBottom:10 }}>{a.title}</h3>
                <p style={{ color:'var(--text-dim)', fontSize:14, lineHeight:1.7 }}>{a.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* HOW PROFIT WORKS */}
      <section style={{ padding:'80px 5%', background:'rgba(201,168,76,0.02)' }}>
        <div style={{ maxWidth:900, margin:'0 auto' }}>
          <SectionHead badge="Revenue Sharing" title="How You Earn" sub="When you invest in a tokenized asset, you earn a share of the profits — rent, interest, or business revenue — paid directly in BRX." />
          <div style={{ display:'flex', flexDirection:'column', gap:0 }}>
            {[
              { step:'01', title:'Choose an Asset', desc:'Browse tokenized properties, gold, bonds, or loans on the HiddenRock marketplace.', color:'var(--gold)' },
              { step:'02', title:'Invest with BRX', desc:'Buy asset tokens using BRX (CRX-20). Each token represents fractional ownership.', color:'var(--emerald)' },
              { step:'03', title:'Asset Generates Revenue', desc:'Property earns rent. Gold appreciates. Bonds pay interest. Loans earn interest from borrowers.', color:'#f59e0b' },
              { step:'04', title:'Profits Distributed', desc:'Revenue is automatically distributed to all token holders proportionally in BRX via smart contracts.', color:'#ef4444' },
              { step:'05', title:'Grow or Exit', desc:'Hold for passive income, or sell tokens on the marketplace anytime. Full liquidity.', color:'#8b5cf6' },
            ].map((s,i) => (
              <div key={i} className="anim" style={{
                display:'flex', gap:28, padding:'28px 0', borderLeft:`2px solid ${s.color}`, paddingLeft:32, marginLeft:16, position:'relative',
              }}>
                <div style={{
                  position:'absolute', left:-14, top:28, width:26, height:26, borderRadius:'50%', background:s.color,
                  display:'flex', alignItems:'center', justifyContent:'center',
                  fontFamily:'JetBrains Mono', fontSize:10, fontWeight:700, color:'#08090d',
                }}>{s.step}</div>
                <div>
                  <h3 style={{ fontFamily:'Playfair Display', fontSize:20, fontWeight:700, color:'#fff', marginBottom:6 }}>{s.title}</h3>
                  <p style={{ color:'var(--text-dim)', fontSize:15, lineHeight:1.7 }}>{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding:'80px 5%' }}>
        <div className="anim" style={{
          maxWidth:900, margin:'0 auto', textAlign:'center', borderRadius:24, padding:'64px 40px',
          background:'linear-gradient(135deg, rgba(201,168,76,0.06), rgba(16,185,129,0.04))',
          border:'1px solid var(--border)',
        }}>
          <h2 style={{ fontFamily:'Playfair Display', fontSize:'clamp(26px,4vw,44px)', fontWeight:800, marginBottom:16 }}>
            <span className="gold-text">Start Investing Today</span>
          </h2>
          <p style={{ color:'var(--text-dim)', fontSize:17, maxWidth:500, margin:'0 auto 32px', lineHeight:1.7 }}>
            Join thousands of investors earning passive income from real-world assets through blockchain tokenization.
          </p>
          <div style={{ display:'flex', gap:14, justifyContent:'center', flexWrap:'wrap' }}>
            <Btn primary href="https://bricsx.io">Get BRX Tokens</Btn>
            <Btn to="/marketplace">Browse Marketplace</Btn>
          </div>
        </div>
      </section>
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   PAGE 2: ABOUT
   ═══════════════════════════════════════════════════ */
const AboutPage = () => {
  useScrollAnim();
  return (
    <div style={{ paddingTop:100 }}>
      <section className="section-pad" style={{ padding:'80px 5%' }}>
        <SectionHead badge="About HiddenRock" title="Unlocking Hidden Value" sub="HiddenRock.io is a real-world asset tokenization platform built on the COREX blockchain. We make traditional investments accessible, transparent, and profitable." />
        <div style={{ maxWidth:1320, margin:'0 auto', display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(300px, 1fr))', gap:24 }}>
          <Card>
            <div style={{ fontFamily:'JetBrains Mono', fontSize:12, color:'var(--emerald)', letterSpacing:2, marginBottom:14 }}>OUR MISSION</div>
            <h3 style={{ fontFamily:'Playfair Display', fontSize:24, fontWeight:700, color:'#fff', marginBottom:14 }}>Democratize Investing</h3>
            <p style={{ color:'var(--text-dim)', fontSize:15, lineHeight:1.8 }}>
              Traditional investments like real estate and gold require huge capital. HiddenRock breaks these assets into affordable tokens on the CRX-20 standard. Now anyone can invest in a luxury property or gold vault starting from just a few BRX tokens.
            </p>
          </Card>
          <Card delay="anim-d2">
            <div style={{ fontFamily:'JetBrains Mono', fontSize:12, color:'var(--gold)', letterSpacing:2, marginBottom:14 }}>OUR VISION</div>
            <h3 style={{ fontFamily:'Playfair Display', fontSize:24, fontWeight:700, color:'#fff', marginBottom:14 }}>The Future of Ownership</h3>
            <p style={{ color:'var(--text-dim)', fontSize:15, lineHeight:1.8 }}>
              We envision a world where every valuable asset — from a building in New York to a gold mine in South Africa — can be owned fractionally by anyone, anywhere. Powered by COREX blockchain and BRX tokens, HiddenRock makes this reality.
            </p>
          </Card>
        </div>
      </section>

      {/* Team / Backing */}
      <section style={{ padding:'80px 5%', background:'rgba(201,168,76,0.02)' }}>
        <div style={{ maxWidth:1320, margin:'0 auto' }}>
          <SectionHead badge="Powered By" title="Our Ecosystem" sub="HiddenRock is built on a robust blockchain ecosystem." />
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(280px, 1fr))', gap:24 }}>
            {[
              { name:'COREX Network', desc:'The Layer 1 blockchain powering HiddenRock. High speed, low fees, CRX-20 token standard.', url:'https://corexnetwork.org', icon:'⬡' },
              { name:'BRICSX.io', desc:'The company behind BRX token. Like Tether issues USDT, BRICSX issues BRX on COREX.', url:'https://bricsx.io', icon:'◆' },
              { name:'BRX Token', desc:'The primary currency for investments on HiddenRock. All profits are paid in BRX (CRX-20).', url:'#', icon:'₿' },
            ].map((p,i) => (
              <Card key={i} delay={`anim-d${i+1}`}>
                <div style={{ width:56, height:56, borderRadius:14, background:'linear-gradient(135deg, rgba(201,168,76,0.15), rgba(201,168,76,0.05))', border:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:24, marginBottom:16 }}>{p.icon}</div>
                <h3 style={{ fontFamily:'Playfair Display', fontSize:22, fontWeight:700, color:'#fff', marginBottom:8 }}>{p.name}</h3>
                <p style={{ color:'var(--text-dim)', fontSize:14, lineHeight:1.7, marginBottom:16 }}>{p.desc}</p>
                {p.url !== '#' && <a href={p.url} target="_blank" rel="noopener noreferrer" style={{ fontFamily:'JetBrains Mono', fontSize:12, color:'var(--gold)', letterSpacing:1 }}>Visit →</a>}
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Different */}
      <section className="section-pad" style={{ padding:'80px 5%' }}>
        <div style={{ maxWidth:1320, margin:'0 auto' }}>
          <SectionHead badge="Why HiddenRock" title="What Makes Us Different" />
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(240px, 1fr))', gap:20 }}>
            {[
              { icon:'🔒', title:'Fully Backed Assets', desc:'Every token is backed by a real, verified asset — property deed, gold certificate, or bond.' },
              { icon:'📊', title:'Transparent Revenue', desc:'All rental income, interest, and profits are tracked on-chain. Fully auditable.' },
              { icon:'⚡', title:'Instant Settlement', desc:'Buy and sell tokens with 1-second finality on COREX. No waiting days for settlement.' },
              { icon:'🌍', title:'Global Access', desc:'Invest in assets across the world from your phone. No borders, no barriers.' },
              { icon:'💎', title:'Low Minimum', desc:'Start investing with as little as 10 BRX. No need for millions to own real estate.' },
              { icon:'🤝', title:'Smart Contract Payouts', desc:'Profits auto-distributed via CRX-20 smart contracts. No middlemen, no delays.' },
            ].map((f,i) => (
              <Card key={i} delay={`anim-d${Math.min(i+1,6)}`} style={{ padding:24 }}>
                <span style={{ fontSize:32, display:'block', marginBottom:12 }}>{f.icon}</span>
                <h4 style={{ fontFamily:'Playfair Display', fontSize:17, fontWeight:700, color:'#fff', marginBottom:8 }}>{f.title}</h4>
                <p style={{ color:'var(--text-dim)', fontSize:13, lineHeight:1.7 }}>{f.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   PAGE 3: ASSETS
   ═══════════════════════════════════════════════════ */
const AssetsPage = () => {
  useScrollAnim();
  const [filter, setFilter] = useState('All');
  const assets = [
    { name:'Dubai Marina Villa', type:'Property', value:'$2,500,000', yield:'12.5%', tokens:'10,000', sold:78, price:'250 BRX' },
    { name:'London Office Complex', type:'Property', value:'$8,200,000', yield:'9.8%', tokens:'32,800', sold:62, price:'250 BRX' },
    { name:'Swiss Gold Vault #7', type:'Gold', value:'$5,000,000', yield:'6.2%', tokens:'50,000', sold:91, price:'100 BRX' },
    { name:'US Treasury Bond Pool', type:'Bond', value:'$1,000,000', yield:'5.1%', tokens:'10,000', sold:100, price:'100 BRX' },
    { name:'SME Loan Portfolio', type:'Loan', value:'$3,000,000', yield:'14.2%', tokens:'30,000', sold:45, price:'100 BRX' },
    { name:'Mumbai Residential Tower', type:'Property', value:'$4,100,000', yield:'11.0%', tokens:'41,000', sold:33, price:'100 BRX' },
    { name:'Gold Bar Collection #12', type:'Gold', value:'$2,000,000', yield:'5.8%', tokens:'20,000', sold:87, price:'100 BRX' },
    { name:'Corporate Bond Fund', type:'Bond', value:'$6,500,000', yield:'7.5%', tokens:'65,000', sold:55, price:'100 BRX' },
  ];
  const filtered = filter === 'All' ? assets : assets.filter(a => a.type === filter);

  return (
    <div style={{ paddingTop:100 }}>
      <section className="section-pad" style={{ padding:'80px 5%' }}>
        <SectionHead badge="Asset Marketplace" title="Explore Tokenized Assets" sub="Browse verified real-world assets available for investment. Each token is backed by a real asset." />
        <div style={{ maxWidth:1320, margin:'0 auto' }}>
          {/* Filters */}
          <div className="anim" style={{ display:'flex', gap:8, justifyContent:'center', marginBottom:40, flexWrap:'wrap' }}>
            {['All','Property','Gold','Bond','Loan'].map(f=>(
              <button key={f} onClick={()=>setFilter(f)} style={{
                padding:'10px 22px', borderRadius:8, border:'1px solid', cursor:'pointer',
                fontFamily:'DM Sans', fontWeight:600, fontSize:13, transition:'all .3s',
                background: filter===f ? 'linear-gradient(135deg, var(--gold-dark), var(--gold))' : 'transparent',
                color: filter===f ? '#08090d' : 'var(--text-dim)',
                borderColor: filter===f ? 'var(--gold)' : 'var(--border)',
              }}>{f}</button>
            ))}
          </div>
          {/* Grid */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(300px, 1fr))', gap:20 }}>
            {filtered.map((a,i) => (
              <Card key={a.name+i}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:16 }}>
                  <span style={{ padding:'4px 10px', borderRadius:6, fontSize:10, fontFamily:'JetBrains Mono', letterSpacing:1, background:'rgba(201,168,76,0.1)', color:'var(--gold)', border:'1px solid var(--border)' }}>{a.type}</span>
                  <span style={{ fontFamily:'JetBrains Mono', fontSize:12, color: a.sold===100 ? 'var(--text-muted)' : 'var(--emerald)' }}>{a.sold===100?'SOLD OUT':'● Live'}</span>
                </div>
                <h3 style={{ fontFamily:'Playfair Display', fontSize:19, fontWeight:700, color:'#fff', marginBottom:8 }}>{a.name}</h3>
                <div style={{ fontFamily:'Playfair Display', fontSize:28, fontWeight:800, marginBottom:16 }} className="gold-text">{a.value}</div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:16 }}>
                  {[
                    { k:'Token Price', v:a.price },
                    { k:'Annual Yield', v:a.yield },
                    { k:'Total Tokens', v:a.tokens },
                    { k:'Funded', v:a.sold+'%' },
                  ].map(r=>(
                    <div key={r.k} style={{ background:'rgba(201,168,76,0.04)', borderRadius:8, padding:'8px 10px', border:'1px solid var(--border)' }}>
                      <div style={{ fontSize:10, color:'var(--text-muted)', fontFamily:'JetBrains Mono', letterSpacing:1 }}>{r.k}</div>
                      <div style={{ fontSize:14, fontWeight:600, color:'var(--gold)', marginTop:2 }}>{r.v}</div>
                    </div>
                  ))}
                </div>
                <div style={{ marginBottom:14 }}>
                  <div style={{ height:4, borderRadius:2, background:'rgba(201,168,76,0.1)' }}>
                    <div style={{ width:`${a.sold}%`, height:'100%', borderRadius:2, background:'linear-gradient(90deg, var(--gold-dark), var(--gold))' }} />
                  </div>
                </div>
                <Btn primary style={{ width:'100%', justifyContent:'center', padding:'12px', fontSize:13, opacity:a.sold===100?.5:1, pointerEvents:a.sold===100?'none':'auto' }}>
                  {a.sold===100?'Sold Out':'Invest Now'}
                </Btn>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   PAGE 4: HOW IT WORKS
   ═══════════════════════════════════════════════════ */
const HowItWorksPage = () => {
  useScrollAnim();
  return (
    <div style={{ paddingTop:100 }}>
      <section className="section-pad" style={{ padding:'80px 5%' }}>
        <SectionHead badge="How It Works" title="From Asset to Income" sub="Understand the complete journey of how real-world assets become tokenized investments on the COREX blockchain." />
        <div style={{ maxWidth:1000, margin:'0 auto' }}>
          {[
            { phase:'PHASE 1', title:'Asset Identification & Verification', desc:'HiddenRock identifies high-quality real-world assets — properties, gold reserves, bonds, or loan portfolios. Each asset undergoes rigorous due diligence, legal verification, and third-party valuation.', items:['Legal audit & title verification','Independent valuation report','Compliance & regulatory check','Insurance & risk assessment'] },
            { phase:'PHASE 2', title:'Tokenization on CRX-20', desc:'The verified asset is tokenized on the COREX blockchain using the CRX-20 standard. A smart contract is deployed that represents fractional ownership of the asset.', items:['Smart contract deployment on COREX','Asset divided into CRX-20 tokens','Token price set based on valuation','KYC/AML compliance integrated'] },
            { phase:'PHASE 3', title:'Token Sale & Investment', desc:'Tokens are listed on the HiddenRock marketplace. Investors can buy tokens using BRX. Each token represents a fractional share of the underlying asset.', items:['Tokens listed on marketplace','Investors buy with BRX (CRX-20)','Fractional ownership granted','Real-time funding tracker'] },
            { phase:'PHASE 4', title:'Revenue Generation', desc:'The asset generates revenue — rent from property, interest from bonds/loans, or appreciation from gold. All revenue is tracked transparently on-chain.', items:['Property earns rental income','Bonds/loans pay interest','Gold appreciates in value','All tracked on COREX blockchain'] },
            { phase:'PHASE 5', title:'Profit Distribution', desc:'Profits are automatically distributed to all token holders proportionally via CRX-20 smart contracts. You receive BRX directly in your wallet — no middlemen.', items:['Smart contract auto-distributes','Profits paid in BRX tokens','Proportional to tokens held','Monthly/quarterly payouts'] },
          ].map((p,i) => (
            <div key={i} className="anim" style={{
              marginBottom:32, background:'var(--card)', border:'1px solid var(--border)', borderRadius:20, padding:36,
              display:'grid', gridTemplateColumns:'auto 1fr', gap:32, alignItems:'start',
            }} className={`anim grid-responsive`}>
              <div style={{
                fontFamily:'JetBrains Mono', fontSize:11, letterSpacing:3, color:'var(--gold)',
                writingMode:'vertical-lr', textOrientation:'mixed', padding:'8px 0',
              }} className="hide-mobile">{p.phase}</div>
              <div>
                <div className="show-mobile" style={{ display:'none', marginBottom:8 }}>
                  <span style={{ fontFamily:'JetBrains Mono', fontSize:11, letterSpacing:3, color:'var(--gold)' }}>{p.phase}</span>
                </div>
                <h3 style={{ fontFamily:'Playfair Display', fontSize:24, fontWeight:700, color:'#fff', marginBottom:12 }}>{p.title}</h3>
                <p style={{ color:'var(--text-dim)', fontSize:15, lineHeight:1.8, marginBottom:20 }}>{p.desc}</p>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }} className="grid-responsive">
                  {p.items.map(item=>(
                    <div key={item} style={{ display:'flex', gap:10, alignItems:'center', padding:'8px 12px', background:'rgba(201,168,76,0.04)', borderRadius:8, border:'1px solid var(--border)' }}>
                      <span style={{ color:'var(--emerald)', fontSize:14 }}>✓</span>
                      <span style={{ fontSize:13, color:'var(--text-dim)' }}>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   PAGE 5: TOKENIZATION
   ═══════════════════════════════════════════════════ */
const TokenizationPage = () => {
  useScrollAnim();
  return (
    <div style={{ paddingTop:100 }}>
      <section className="section-pad" style={{ padding:'80px 5%' }}>
        <SectionHead badge="Tokenization" title="CRX-20 Asset Tokenization" sub="Understanding how real-world assets are converted into blockchain tokens on the COREX Network." />
        <div style={{ maxWidth:1320, margin:'0 auto' }}>
          {/* Comparison */}
          <div className="anim" style={{ marginBottom:60 }}>
            <div style={{ borderRadius:16, overflow:'hidden', border:'1px solid var(--border)' }}>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead>
                  <tr style={{ background:'rgba(201,168,76,0.06)' }}>
                    {['Feature','Traditional Investment','HiddenRock Tokenization'].map(h=>(
                      <th key={h} style={{ padding:'16px 20px', textAlign:'left', fontFamily:'Playfair Display', fontSize:14, fontWeight:700, color: h.includes('HiddenRock') ? 'var(--gold)' : 'var(--text-dim)', borderBottom:'1px solid var(--border)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Minimum Investment','$100,000+','10 BRX (~$10)'],
                    ['Settlement Time','Days to Weeks','1 Second'],
                    ['Fees','3-6% Brokerage','Near Zero'],
                    ['Liquidity','Low (Lock-in periods)','24/7 Trading'],
                    ['Transparency','Limited','Full On-Chain'],
                    ['Global Access','Country Restricted','Borderless'],
                    ['Revenue Distribution','Manual, Delayed','Auto Smart Contract'],
                  ].map((r,i)=>(
                    <tr key={i} style={{ borderBottom:'1px solid var(--border)' }}>
                      {r.map((c,j)=>(
                        <td key={j} style={{ padding:'14px 20px', fontSize:14, color: j===2 ? 'var(--gold)' : 'var(--text-dim)', fontWeight: j===2 ? 600 : 400, fontFamily: j===0 ? 'Playfair Display' : 'DM Sans' }}>{c}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Token Standards */}
          <SectionHead badge="Standards" title="CRX-20 Token Standard" sub="Just like BEP-20 powers BSC and TRC-20 powers TRON, CRX-20 is the native token standard for the COREX blockchain." />
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(300px, 1fr))', gap:24 }}>
            {[
              { title:'What is CRX-20?', desc:'CRX-20 is the token standard on COREX Network — similar to ERC-20 on Ethereum. It defines how tokens are created, transferred, and managed on the COREX blockchain. All HiddenRock asset tokens are CRX-20 tokens.' },
              { title:'What is BRX?', desc:'BRX is the primary utility token issued by BRICSX.io on the CRX-20 standard. It is the currency used for investing in tokenized assets on HiddenRock. All profits and returns are paid in BRX.' },
              { title:'Asset-Backed Tokens', desc:'Each tokenized asset on HiddenRock has its own CRX-20 token (e.g., DUB-VILLA for Dubai Villa). These tokens represent fractional ownership and are fully backed by verified real-world assets.' },
            ].map((c,i) => (
              <Card key={i} delay={`anim-d${i+1}`}>
                <h3 style={{ fontFamily:'Playfair Display', fontSize:20, fontWeight:700, color:'#fff', marginBottom:12 }}>{c.title}</h3>
                <p style={{ color:'var(--text-dim)', fontSize:14, lineHeight:1.8 }}>{c.desc}</p>
              </Card>
            ))}
          </div>

          {/* Smart Contract Flow */}
          <div className="anim" style={{ marginTop:60, textAlign:'center' }}>
            <h3 style={{ fontFamily:'Playfair Display', fontSize:24, fontWeight:700, marginBottom:32 }} className="gold-text">Smart Contract Flow</h3>
            <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:0 }}>
              {[
                { label:'Real World Asset', sub:'Property / Gold / Bond', color:'var(--gold)' },
                { label:'HiddenRock Platform', sub:'Verification & Tokenization', color:'var(--emerald)' },
                { label:'CRX-20 Smart Contract', sub:'On COREX Blockchain', color:'#f59e0b' },
                { label:'Asset Tokens Minted', sub:'Fractional Ownership', color:'#8b5cf6' },
                { label:'Investors Buy with BRX', sub:'Via BRICSX.io', color:'#ef4444' },
                { label:'Revenue → BRX Payouts', sub:'Auto Distribution', color:'var(--gold)' },
              ].map((s,i)=>(
                <React.Fragment key={i}>
                  <div style={{ padding:'16px 32px', borderRadius:12, border:`2px solid ${s.color}`, background:'var(--card)', minWidth:280, display:'inline-block' }}>
                    <div style={{ fontFamily:'Playfair Display', fontSize:16, fontWeight:700, color:s.color }}>{s.label}</div>
                    <div style={{ fontSize:12, color:'var(--text-dim)', marginTop:4 }}>{s.sub}</div>
                  </div>
                  {i < 5 && <div style={{ width:2, height:32, background:`linear-gradient(to bottom, ${s.color}, transparent)` }} />}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   PAGE 6: INVESTORS
   ═══════════════════════════════════════════════════ */
const InvestorsPage = () => {
  useScrollAnim();
  return (
    <div style={{ paddingTop:100 }}>
      <section className="section-pad" style={{ padding:'80px 5%' }}>
        <SectionHead badge="For Investors" title="Why Invest with HiddenRock?" sub="Earn passive income from real-world assets. Property rent, bond interest, and loan yields — all paid in BRX." />
        <div style={{ maxWidth:1320, margin:'0 auto' }}>
          {/* Benefits */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(280px, 1fr))', gap:20, marginBottom:60 }}>
            {[
              { icon:'💰', title:'Passive Income', desc:'Earn monthly/quarterly payouts from rental income, bond interest, and loan yields — automatically in BRX.', highlight:'Up to 18% APY' },
              { icon:'🔐', title:'Asset-Backed Security', desc:'Every investment is backed by a verified, insured real-world asset. Your tokens have real value.', highlight:'100% Backed' },
              { icon:'📈', title:'Capital Appreciation', desc:'As asset values rise, your token value increases too. Sell at profit anytime on the marketplace.', highlight:'Growth + Income' },
              { icon:'⚡', title:'Instant Liquidity', desc:'Unlike traditional real estate, you can sell your tokens 24/7 on the COREX-powered marketplace.', highlight:'24/7 Trading' },
              { icon:'🌐', title:'Global Diversification', desc:'Invest in assets across Dubai, London, Mumbai, Switzerland, and more from one platform.', highlight:'50+ Countries' },
              { icon:'🤖', title:'Smart Contract Payouts', desc:'No middlemen. Revenue is distributed via CRX-20 smart contracts — transparent, automatic, trustless.', highlight:'Zero Middlemen' },
            ].map((b,i) => (
              <Card key={i} delay={`anim-d${Math.min(i+1,6)}`}>
                <span style={{ fontSize:36, display:'block', marginBottom:14 }}>{b.icon}</span>
                <h3 style={{ fontFamily:'Playfair Display', fontSize:19, fontWeight:700, color:'#fff', marginBottom:8 }}>{b.title}</h3>
                <p style={{ color:'var(--text-dim)', fontSize:14, lineHeight:1.7, marginBottom:12 }}>{b.desc}</p>
                <span style={{ fontFamily:'JetBrains Mono', fontSize:11, letterSpacing:2, color:'var(--gold)', padding:'4px 10px', border:'1px solid var(--border)', borderRadius:6 }}>{b.highlight}</span>
              </Card>
            ))}
          </div>

          {/* Example ROI */}
          <SectionHead badge="Example" title="Investment Scenario" sub="See how a $10,000 investment in a tokenized property could grow over time." />
          <div className="anim" style={{ maxWidth:800, margin:'0 auto', background:'var(--card)', border:'1px solid var(--border)', borderRadius:20, padding:40 }}>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(180px, 1fr))', gap:20 }}>
              {[
                { k:'Investment', v:'$10,000', s:'40,000 BRX' },
                { k:'Asset', v:'Dubai Villa', s:'Token: DUB-VILLA' },
                { k:'Annual Yield', v:'12.5%', s:'From Rental Income' },
                { k:'Year 1 Earnings', v:'$1,250', s:'5,000 BRX' },
                { k:'Year 3 Total', v:'$3,750', s:'15,000 BRX earned' },
                { k:'Year 5 Total', v:'$6,250+', s:'+ Capital Growth' },
              ].map(r=>(
                <div key={r.k} style={{ background:'rgba(201,168,76,0.04)', borderRadius:12, padding:'16px', border:'1px solid var(--border)', textAlign:'center' }}>
                  <div style={{ fontSize:11, color:'var(--text-muted)', fontFamily:'JetBrains Mono', letterSpacing:1, marginBottom:6 }}>{r.k}</div>
                  <div style={{ fontFamily:'Playfair Display', fontSize:22, fontWeight:800, marginBottom:4 }} className="gold-text">{r.v}</div>
                  <div style={{ fontSize:12, color:'var(--text-dim)' }}>{r.s}</div>
                </div>
              ))}
            </div>
            <div style={{ marginTop:24, textAlign:'center' }}>
              <Btn primary href="https://bricsx.io">Start Investing →</Btn>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   PAGE 7: MARKETPLACE
   ═══════════════════════════════════════════════════ */
const MarketplacePage = () => {
  useScrollAnim();
  const listings = [
    { name:'Dubai Marina Luxury Villa', type:'Property', value:'$2.5M', yield:'12.5%', funded:78, status:'Live', tags:['CRX-20','Rental Income'] },
    { name:'Swiss Gold Vault #7', type:'Gold', value:'$5.0M', yield:'6.2%', funded:91, status:'Live', tags:['CRX-20','Physical Gold'] },
    { name:'London Canary Wharf Office', type:'Property', value:'$8.2M', yield:'9.8%', funded:62, status:'Live', tags:['CRX-20','Commercial'] },
    { name:'US Treasury Bond Pool A', type:'Bond', value:'$1.0M', yield:'5.1%', funded:100, status:'Sold', tags:['CRX-20','Fixed Income'] },
    { name:'India SME Loan Portfolio', type:'Loan', value:'$3.0M', yield:'14.2%', funded:45, status:'Live', tags:['CRX-20','High Yield'] },
    { name:'Singapore Condo Tower', type:'Property', value:'$6.7M', yield:'10.5%', funded:28, status:'New', tags:['CRX-20','Residential'] },
    { name:'African Gold Reserve #3', type:'Gold', value:'$4.0M', yield:'7.1%', funded:55, status:'Live', tags:['CRX-20','Physical Gold'] },
    { name:'European Corp Bond Fund', type:'Bond', value:'$10M', yield:'7.5%', funded:72, status:'Live', tags:['CRX-20','Diversified'] },
    { name:'Australia Property Trust', type:'Property', value:'$12M', yield:'8.9%', funded:15, status:'New', tags:['CRX-20','REIT'] },
  ];

  return (
    <div style={{ paddingTop:100 }}>
      <section className="section-pad" style={{ padding:'80px 5%' }}>
        <SectionHead badge="Marketplace" title="Invest in Tokenized Assets" sub="Browse all available tokenized real-world assets. Buy tokens with BRX and start earning." />
        <div style={{ maxWidth:1320, margin:'0 auto' }}>
          {/* Summary bar */}
          <div className="anim" style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(150px, 1fr))', gap:16, marginBottom:40 }}>
            {[
              { l:'Total Assets', v:'9' },{ l:'Total Value', v:'$52.4M' },{ l:'Avg Yield', v:'10.2%' },{ l:'Active', v:'7 Live' },
            ].map(s=>(
              <div key={s.l} style={{ background:'var(--card)', border:'1px solid var(--border)', borderRadius:12, padding:'16px 20px', textAlign:'center' }}>
                <div style={{ fontSize:11, color:'var(--text-muted)', fontFamily:'JetBrains Mono', letterSpacing:1, marginBottom:4 }}>{s.l}</div>
                <div style={{ fontFamily:'Playfair Display', fontSize:22, fontWeight:800 }} className="gold-text">{s.v}</div>
              </div>
            ))}
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(340px, 1fr))', gap:20 }}>
            {listings.map((l,i) => (
              <Card key={i}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:14 }}>
                  <span style={{ padding:'4px 10px', borderRadius:6, fontSize:10, fontFamily:'JetBrains Mono', letterSpacing:1, background:'rgba(201,168,76,0.08)', color:'var(--gold)', border:'1px solid var(--border)' }}>{l.type}</span>
                  <span style={{
                    fontFamily:'JetBrains Mono', fontSize:11, letterSpacing:1,
                    color: l.status==='New' ? '#8b5cf6' : l.status==='Sold' ? 'var(--text-muted)' : 'var(--emerald)',
                  }}>● {l.status}</span>
                </div>
                <h3 style={{ fontFamily:'Playfair Display', fontSize:18, fontWeight:700, color:'#fff', marginBottom:6 }}>{l.name}</h3>
                <div style={{ display:'flex', gap:20, marginBottom:14 }}>
                  <div><span style={{ fontSize:12, color:'var(--text-muted)' }}>Value</span><div style={{ fontFamily:'Playfair Display', fontSize:20, fontWeight:700 }} className="gold-text">{l.value}</div></div>
                  <div><span style={{ fontSize:12, color:'var(--text-muted)' }}>Yield</span><div style={{ fontFamily:'Playfair Display', fontSize:20, fontWeight:700, color:'var(--emerald)' }}>{l.yield}</div></div>
                </div>
                <div style={{ marginBottom:14 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:'var(--text-dim)', marginBottom:4 }}>
                    <span>Funded</span><span style={{color:'var(--gold)'}}>{l.funded}%</span>
                  </div>
                  <div style={{ height:4, borderRadius:2, background:'rgba(201,168,76,0.1)' }}>
                    <div style={{ width:`${l.funded}%`, height:'100%', borderRadius:2, background:'linear-gradient(90deg, var(--gold-dark), var(--gold))' }} />
                  </div>
                </div>
                <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:14 }}>
                  {l.tags.map(t=><span key={t} style={{ padding:'3px 8px', borderRadius:4, fontSize:9, fontFamily:'JetBrains Mono', letterSpacing:1, border:'1px solid var(--border)', color:'var(--text-dim)' }}>{t}</span>)}
                </div>
                <Btn primary style={{ width:'100%', justifyContent:'center', padding:'11px', fontSize:12, opacity:l.status==='Sold'?.5:1, pointerEvents:l.status==='Sold'?'none':'auto' }}>
                  {l.status==='Sold'?'Sold Out':l.status==='New'?'Coming Soon':'Invest Now'}
                </Btn>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   PAGE 8: CONTACT
   ═══════════════════════════════════════════════════ */
const ContactPage = () => {
  useScrollAnim();
  const [form, setForm] = useState({ name:'', email:'', subject:'', message:'' });
  const [sent, setSent] = useState(false);
  const submit = () => { setSent(true); setTimeout(()=>setSent(false), 3000); };

  return (
    <div style={{ paddingTop:100 }}>
      <section className="section-pad" style={{ padding:'80px 5%' }}>
        <SectionHead badge="Contact" title="Get in Touch" sub="Questions about tokenization, investing, or partnerships? We'd love to hear from you." />
        <div style={{ maxWidth:1100, margin:'0 auto', display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(300px, 1fr))', gap:40 }}>
          <div>
            {[
              { icon:'📧', title:'General', info:'hello@hiddenrock.io' },
              { icon:'💼', title:'Partnerships', info:'partners@hiddenrock.io' },
              { icon:'📞', title:'Support', info:'support@hiddenrock.io' },
              { icon:'🌐', title:'Website', info:'hiddenrock.io' },
            ].map((c,i) => (
              <Card key={i} style={{ padding:20, marginBottom:12 }}>
                <div style={{ display:'flex', gap:14, alignItems:'center' }}>
                  <div style={{ width:44, height:44, borderRadius:10, background:'rgba(201,168,76,0.08)', border:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, flexShrink:0 }}>{c.icon}</div>
                  <div>
                    <div style={{ fontFamily:'Playfair Display', fontSize:15, fontWeight:700, color:'#fff' }}>{c.title}</div>
                    <div style={{ fontFamily:'JetBrains Mono', fontSize:12, color:'var(--gold)', marginTop:2 }}>{c.info}</div>
                  </div>
                </div>
              </Card>
            ))}
            <div className="anim" style={{ marginTop:24 }}>
              <h4 style={{ fontFamily:'Playfair Display', fontSize:15, fontWeight:700, color:'var(--gold)', marginBottom:14 }}>Follow Us</h4>
              <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
                {['Twitter','Telegram','Discord','LinkedIn','Medium'].map(s=>(
                  <span key={s} style={{ padding:'8px 14px', borderRadius:8, border:'1px solid var(--border)', fontSize:13, color:'var(--text-dim)', cursor:'pointer', transition:'all .3s' }}
                    onMouseOver={e=>{e.target.style.borderColor='var(--gold)';e.target.style.color='var(--gold)';}}
                    onMouseOut={e=>{e.target.style.borderColor='var(--border)';e.target.style.color='var(--text-dim)';}}>{s}</span>
                ))}
              </div>
            </div>
          </div>

          <div className="anim" style={{ background:'var(--card)', border:'1px solid var(--border)', borderRadius:20, padding:36 }}>
            <h3 style={{ fontFamily:'Playfair Display', fontSize:22, fontWeight:700, color:'#fff', marginBottom:24 }}>Send a Message</h3>
            {sent ? (
              <div style={{ textAlign:'center', padding:'50px 20px', animation:'scaleIn .5s ease' }}>
                <div style={{ fontSize:48, marginBottom:16 }}>✅</div>
                <h4 style={{ fontFamily:'Playfair Display', fontSize:20, marginBottom:8 }} className="gold-text">Message Sent!</h4>
                <p style={{ color:'var(--text-dim)' }}>We'll respond within 24 hours.</p>
              </div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
                {['name','email','subject'].map(f=>(
                  <div key={f}>
                    <label style={{ fontSize:13, fontWeight:500, color:'var(--text-dim)', display:'block', marginBottom:5, textTransform:'capitalize' }}>{f}</label>
                    <input value={form[f]} onChange={e=>setForm({...form,[f]:e.target.value})} placeholder={f==='email'?'you@example.com':''} style={{
                      width:'100%', padding:'12px 14px', borderRadius:10, background:'rgba(8,9,13,0.6)', border:'1px solid var(--border)', color:'var(--text)', fontFamily:'DM Sans', fontSize:14, outline:'none', transition:'border-color .3s',
                    }} onFocus={e=>e.target.style.borderColor='var(--gold)'} onBlur={e=>e.target.style.borderColor='var(--border)'} />
                  </div>
                ))}
                <div>
                  <label style={{ fontSize:13, fontWeight:500, color:'var(--text-dim)', display:'block', marginBottom:5 }}>Message</label>
                  <textarea rows={4} value={form.message} onChange={e=>setForm({...form,message:e.target.value})} style={{
                    width:'100%', padding:'12px 14px', borderRadius:10, background:'rgba(8,9,13,0.6)', border:'1px solid var(--border)', color:'var(--text)', fontFamily:'DM Sans', fontSize:14, outline:'none', resize:'vertical', transition:'border-color .3s',
                  }} onFocus={e=>e.target.style.borderColor='var(--gold)'} onBlur={e=>e.target.style.borderColor='var(--border)'} />
                </div>
                <Btn primary onClick={submit} style={{ justifyContent:'center', marginTop:8 }}>Send Message</Btn>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   SCROLL TO TOP
   ═══════════════════════════════════════════════════ */
const ScrollTop = () => { const { pathname } = useLocation(); useEffect(()=>window.scrollTo(0,0),[pathname]); return null; };

/* ═══════════════════════════════════════════════════
   APP
   ═══════════════════════════════════════════════════ */
export default function App() {
  return (
    <Router>
      <Styles />
      <div className="grain-overlay" />
      <ScrollTop />
      <div style={{ position:'relative', zIndex:1 }}>
        <Navbar />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/assets" element={<AssetsPage />} />
          <Route path="/how-it-works" element={<HowItWorksPage />} />
          <Route path="/tokenization" element={<TokenizationPage />} />
          <Route path="/investors" element={<InvestorsPage />} />
          <Route path="/marketplace" element={<MarketplacePage />} />
          <Route path="/contact" element={<ContactPage />} />
        </Routes>
        <Footer />
      </div>
    </Router>
  );
}
